import { FlowSpecDto } from '@ampsec/platform-client';
import { FlowSpecTriggerItem } from './targetFindingDisplay.util';

export type TagFilterBuckets = {
  any: string[];
  all: string[];
  not: string[];
};

export type FindingTriggerInput = {
  id: string;
  displayValue?: string;
  cid?: string;
  eventType?: string;
};

export type TagSpecLike = {
  id: string;
  name: string;
};

export type TriggerFilterPayload = {
  trigger: Array<Partial<FlowSpecTriggerItem>>;
  tags?: Partial<TagFilterBuckets>;
};

export function emptyTagFilterBuckets(): TagFilterBuckets {
  return {
    any: [],
    all: [],
    not: [],
  };
}

function normalizeTagIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter((id): id is string => typeof id === 'string')
    .map((id) => id.trim())
    .filter(Boolean);
}

function mergeTagBuckets(
  target: TagFilterBuckets,
  source: Partial<TagFilterBuckets> | undefined,
): TagFilterBuckets {
  if (!source) {
    return target;
  }

  return {
    any: Array.from(new Set([...target.any, ...normalizeTagIds(source.any)])),
    all: Array.from(new Set([...target.all, ...normalizeTagIds(source.all)])),
    not: Array.from(new Set([...target.not, ...normalizeTagIds(source.not)])),
  };
}

export function parseTagBucketsFromTriggerFilter(
  triggerFilter: FlowSpecDto['triggerFilter'] | undefined,
): TagFilterBuckets {
  if (!triggerFilter) {
    return emptyTagFilterBuckets();
  }

  if (triggerFilter.tags) {
    return mergeTagBuckets(emptyTagFilterBuckets(), {
      any: triggerFilter.tags.any,
      all: triggerFilter.tags.all,
      not: triggerFilter.tags.not,
    });
  }

  let buckets = emptyTagFilterBuckets();
  for (const trigger of triggerFilter.trigger ?? []) {
    const legacyTags = (trigger as FlowSpecTriggerItem & { tags?: Partial<TagFilterBuckets> }).tags;
    if (!legacyTags) {
      continue;
    }
    buckets = mergeTagBuckets(buckets, legacyTags);
  }

  return buckets;
}

export function cleanTagFilterBuckets(buckets: TagFilterBuckets): Partial<TagFilterBuckets> {
  const cleaned: Partial<TagFilterBuckets> = {};

  const any = normalizeTagIds(buckets.any);
  if (any.length > 0) {
    cleaned.any = any;
  }

  const all = normalizeTagIds(buckets.all);
  if (all.length > 0) {
    cleaned.all = all;
  }

  const not = normalizeTagIds(buckets.not);
  if (not.length > 0) {
    cleaned.not = not;
  }

  return cleaned;
}

export function buildTriggerFilterPayload(
  findingTriggers: FindingTriggerInput[],
  tagBuckets: TagFilterBuckets,
): TriggerFilterPayload {
  const cleanedTags = cleanTagFilterBuckets(tagBuckets);
  const hasTags = Object.keys(cleanedTags).length > 0;

  const trigger = findingTriggers.map((findingTrigger) => ({
    id: findingTrigger.id,
    displayValue: findingTrigger.displayValue,
    cid: findingTrigger.cid,
    eventType: findingTrigger.eventType,
  }));

  if (trigger.length === 0) {
    if (cleanedTags.any?.length || cleanedTags.all?.length) {
      return { trigger: [], tags: cleanedTags };
    }
    return { trigger: [] };
  }

  return {
    trigger,
    ...(hasTags ? { tags: cleanedTags } : {}),
  };
}

/** @deprecated Use buildTriggerFilterPayload — tags live on triggerFilter, not each trigger entry. */
export function buildTriggersWithTags(
  findingTriggers: FindingTriggerInput[],
  tagBuckets: TagFilterBuckets,
): Array<Partial<FlowSpecTriggerItem>> {
  return buildTriggerFilterPayload(findingTriggers, tagBuckets).trigger;
}

export function formatTagFiltersPlainLanguage(
  buckets: TagFilterBuckets,
  tagSpecIdToName: Record<string, string>,
): string {
  const sections: string[] = [];

  const formatIds = (ids: string[]) =>
    ids
      .map((id) => tagSpecIdToName[id] ?? id)
      .join(', ');

  if (buckets.any.length > 0) {
    sections.push(`Includes any of: ${formatIds(buckets.any)}`);
  }

  if (buckets.all.length > 0) {
    sections.push(`Includes all of: ${formatIds(buckets.all)}`);
  }

  if (buckets.not.length > 0) {
    sections.push(`Excludes any of: ${formatIds(buckets.not)}`);
  }

  return sections.length > 0 ? sections.join('; ') : '';
}

export function buildTagSpecIdToNameLookup(tagSpecs: TagSpecLike[]): Record<string, string> {
  const idToName: Record<string, string> = {};

  for (const tagSpec of tagSpecs) {
    if (tagSpec?.id) {
      idToName[tagSpec.id] = tagSpec.name;
    }
  }

  return idToName;
}
